import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle,
  Shield,
  Phone,
  MapPin,
  Calendar,
  X,
  CheckCircle,
} from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import CardSwiper from "@/components/CardSwiper";

export default function CardStolen() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    lastLocation: "",
    lastUsedDate: "",
    lastUsedTime: "",
    description: "",
    contactPhone: "",
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    setShowConfirmModal(true);
  };

  const confirmReport = () => {
    setIsReporting(true);
    setShowConfirmModal(false);

    // Simulate API call
    setTimeout(() => {
      setIsReporting(false);
      setIsCompleted(true);
    }, 2000);
  };

  // Completed Screen
  if (isCompleted) {
    return (
      <div className="min-h-screen bg-background">
        <Header showBack={false} />

        <div className="max-w-md mx-auto px-3 pb-20">
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] space-y-6">
            {/* Success Icon */}
            <div className="p-4 bg-green-100 dark:bg-green-900/20 rounded-full">
              <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400" />
            </div>

            {/* Success Message */}
            <div className="text-center space-y-3">
              <h2 className="text-2xl font-bold">Card Reported as Stolen</h2>
              <p className="text-muted-foreground">
                Your card has been permanently blocked and cannot be reactivated
              </p>
            </div>

            {/* Reference Number */}
            {/* <Card className="bg-muted/50 w-full">
              <CardContent className="p-4">
                <p className="text-sm text-center">
                  Reference Number:{" "}
                  <span className="font-bold">
                    STL-2024-{Math.random().toString().substr(2, 6)}
                  </span>
                </p>
              </CardContent>
            </Card> */}

            {/* Next Steps */}
            {/* <Card className="w-full">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Next Steps:</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex gap-2">
                    <span className="text-primary">1.</span>
                    <span>
                      A new card will be issued and sent to your registered
                      address
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">2.</span>
                    <span>You will receive an email confirmation shortly</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-primary">3.</span>
                    <span>
                      Contact support if you need immediate assistance
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card> */}

            {/* Action Button */}
            <Button
              onClick={() => navigate("/mycard")}
              className="mt-8 w-full h-12"
            >
              Back to My Cards
            </Button>
          </div>
        </div>

        <BottomBar />
      </div>
    );
  }

  // Main Report Form
  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/mycard")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Card Display */}
        <div className="mt-4">
          <CardSwiper userEmail="user@example.com" />
        </div>

        {/* Warning Banner */}
        <Card className="mt-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                  Important: This action cannot be undone
                </p>
                <p className="text-xs text-red-700 dark:text-red-300">
                  Once reported as stolen, this card will be permanently
                  blocked.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Form */}
        <Card className="mt-6 bg-card/80">
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Security Notice */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex gap-2">
                  <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    For your security, all transactions on this card have been
                    blocked immediately.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleSubmit}
                  className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isReporting ? "Reporting..." : "Report as Stolen"}
                </Button>
                {/* <Button
                  variant="outline"
                  onClick={() => navigate("/mycard")}
                  className="w-full h-12"
                >
                  Cancel
                </Button> */}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <BottomBar />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4">
          <div className="bg-background rounded-[20px] shadow-lg p-6 w-full max-w-[360px]">
            {/* Close Button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-1 hover:bg-muted rounded-full"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-6">
              {/* Icon and Title */}
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                  <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-semibold">
                  Confirm Stolen Card Report
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action is permanent and cannot be reversed
                </p>
              </div>

              {/* Warning */}
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-center text-red-800 dark:text-red-200">
                  Your card will be immediately and permanently blocked. A new
                  card will be issued.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 h-11"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmReport}
                  disabled={isReporting}
                  className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isReporting ? "Reporting..." : "Confirm Report"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
